import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaBalanceScale, FaPlay, FaSearch } from 'react-icons/fa';
import { showError, showSuccess } from '../utils/toast';

const getIstanbulToday = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Istanbul'
}).format(new Date());

const formatNumber = value => Number.isFinite(value)
  ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(value)
  : '—';

const formatPercent = value => Number.isFinite(value)
  ? `${value > 0 ? '+' : ''}${value.toFixed(2)}%`
  : '—';

const statusClasses = {
  OK: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  OUTLIER: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  TCMB_MISSING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  TRUNCGIL_MISSING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  BOTH_MISSING: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
};

const SourceComparison = () => {
  const [date, setDate] = useState(getIstanbulToday());
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyOutliers, setOnlyOutliers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const applyResponse = data => {
    setRecords(data.records || []);
    setSummary(data.summary || null);
    if (data.date) setDate(data.date);
  };

  useEffect(() => {
    const loadLatest = async () => {
      try {
        const { data } = await axios.get('/api/admin/source-comparison/latest');
        applyResponse(data);
      } catch (error) {
        showError(error.response?.data?.message || 'Kaynak karşılaştırması yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    loadLatest();
  }, []);

  const loadDate = async nextDate => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/source-comparison', { params: { date: nextDate } });
      applyResponse(data);
    } catch (error) {
      showError(error.response?.data?.message || 'Seçilen tarih yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const runComparison = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post('/api/admin/run-source-comparison');
      applyResponse(data);
      showSuccess('Bugünkü kaynak karşılaştırması tamamlandı');
    } catch (error) {
      showError(error.response?.data?.message || 'Karşılaştırma çalıştırılamadı');
    } finally {
      setRunning(false);
    }
  };

  const filteredRecords = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('tr-TR');
    return records.filter(record => {
      const matchesSearch = !query || record.symbol.toLocaleLowerCase('tr-TR').includes(query)
        || record.displayName.toLocaleLowerCase('tr-TR').includes(query);
      return matchesSearch && (!onlyOutliers || record.status === 'OUTLIER');
    });
  }, [records, search, onlyOutliers]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-8">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 mb-6">
          <FaArrowLeft /> Admin Paneli
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <FaBalanceScale className="text-indigo-600" /> Kaynak Karşılaştırması
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-2">TCMB gösterge kurları ile Trunçgil güncel kurlarını karşılaştırın.</p>
          </div>
          <button
            onClick={runComparison}
            disabled={running}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 font-semibold text-white disabled:opacity-50"
          >
            <FaPlay className={running ? 'animate-pulse' : ''} />
            {running ? 'Çalıştırılıyor...' : 'Bugünkü Karşılaştırmayı Çalıştır'}
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          {[
            ['Tarih', summary?.date || date],
            ['Karşılaştırılan', summary?.comparedSymbolCount ?? 0],
            ['Ort. Alış Farkı', formatPercent(summary?.averageBuyDiffPercent)],
            ['Ort. Satış Farkı', formatPercent(summary?.averageSellDiffPercent)],
            ['Outlier', summary?.outlierCount ?? 0],
            ['Son Çalışma', summary?.latestComparisonAt ? new Date(summary.latestComparisonAt).toLocaleString('tr-TR') : '—']
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-4 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
              <p className="mt-2 font-bold text-slate-900 dark:text-white break-words">{value}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-5 mb-6 flex flex-col md:flex-row gap-4 md:items-center">
          <input
            type="date"
            value={date}
            max={getIstanbulToday()}
            onChange={event => {
              setDate(event.target.value);
              loadDate(event.target.value);
            }}
            className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2"
          />
          <label className="relative flex-1">
            <FaSearch className="absolute left-3 top-3 text-slate-400" />
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Sembol veya varlık ara"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 pl-10 pr-3 py-2"
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={onlyOutliers} onChange={event => setOnlyOutliers(event.target.checked)} />
            Sadece outlier
          </label>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm">
          <table className="min-w-[1250px] w-full text-sm">
            <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
              <tr>{['Sembol', 'Varlık Adı', 'TCMB Alış', 'Trunçgil Alış', 'Alış Farkı', 'Alış Farkı %', 'TCMB Satış', 'Trunçgil Satış', 'Satış Farkı', 'Satış Farkı %', 'Durum'].map(title => <th key={title} className="px-4 py-3 text-left whitespace-nowrap">{title}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {!loading && filteredRecords.map(record => (
                <tr key={record._id} className="hover:bg-slate-50 dark:hover:bg-white/5">
                  <td className="px-4 py-3 font-bold">{record.symbol}</td>
                  <td className="px-4 py-3">{record.displayName}</td>
                  <td className="px-4 py-3">{formatNumber(record.tcmbBuyPrice)}</td>
                  <td className="px-4 py-3">{formatNumber(record.truncgilBuyPrice)}</td>
                  <td className="px-4 py-3">{formatNumber(record.buyDiff)}</td>
                  <td className="px-4 py-3">{formatPercent(record.buyDiffPercent)}</td>
                  <td className="px-4 py-3">{formatNumber(record.tcmbSellPrice)}</td>
                  <td className="px-4 py-3">{formatNumber(record.truncgilSellPrice)}</td>
                  <td className="px-4 py-3">{formatNumber(record.sellDiff)}</td>
                  <td className="px-4 py-3">{formatPercent(record.sellDiffPercent)}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusClasses[record.status]}`}>{record.status}</span></td>
                </tr>
              ))}
              {!loading && filteredRecords.length === 0 && <tr><td colSpan="11" className="px-4 py-12 text-center text-slate-500">Bu tarih ve filtreler için kayıt yok.</td></tr>}
              {loading && <tr><td colSpan="11" className="px-4 py-12 text-center text-slate-500">Yükleniyor...</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SourceComparison;
